import { useState } from "react";
import { todayLocal } from "../utils/dateLocal";
import axios from "axios";
import DateInput from "./DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MontantInput = ({ value, onChange, testId }) => (
  <input
    type="text"
    inputMode="numeric"
    className="calc-input-number"
    style={{ width: 220 }}
    value={`${(value || 0).toLocaleString('fr-CA')} $`}
    onChange={e => {
      const n = parseInt(e.target.value.replace(/\D/g, ''), 10);
      onChange(isNaN(n) ? 0 : n);
    }}
    data-testid={testId}
  />
);

const CalculatriceModal = ({ onClose }) => {
  const [calcData, setCalcData] = useState({
    dateCalcul: todayLocal(),
    categorieReq: 0, revenu: 0, biens: 0, liquidite: 0,
    regionEloignee: false, inclutResidence: false,
    revenuRepute: null, contributionMax: null
  });

  const categoriesCalc = [
    { value: 0, label: 'Personne seule' },
    { value: 1, label: 'Famille - Personne seule, 1 enfant' },
    { value: 2, label: 'Famille - Personne seule, 2 enfants ou +' },
    { value: 3, label: 'Famille - Conjoint sans enfant' },
    { value: 4, label: 'Famille - Conjoint avec 1 enfant' },
    { value: 5, label: 'Famille - Conjoint avec 2 enfants ou +' },
  ];

  const handleCalcChange = (field, value) => {
    setCalcData(prev => ({ ...prev, [field]: value, revenuRepute: null, contributionMax: null }));
  };

  const handleInitialiser = () => {
    setCalcData({ dateCalcul: todayLocal(), categorieReq: 0, revenu: 0, biens: 0, liquidite: 0, regionEloignee: false, inclutResidence: false, revenuRepute: null, contributionMax: null });
  };

  const handleCalculerAdmissibilite = async () => {
    // Ne pas calculer si aucun montant saisi (tous à 0)
    if (!calcData.revenu && !calcData.biens && !calcData.liquidite) {
      setCalcData(prev => ({ ...prev, revenuRepute: null, contributionMax: null }));
      return;
    }
    try {
      const valRegEloiMulti = 1.2;
      const valDeducSansMaison = 47500;
      const valDeducMaison = 90000;
      const valLiquideSeul = 2500;
      const valLiquideFamille = 5000;
      const valActifMulti = 0.1;

      const response = await axios.get(`${API}/reference/baremes`);
      const baremes = response.data.volets;
      const dateCalc = new Date(calcData.dateCalcul);
      let baremeApplicable = null;
      for (const bareme of baremes) {
        const debut = new Date(bareme.DateDebut);
        const fin = new Date(bareme.DateFin);
        if (bareme.NoCateg === calcData.categorieReq && dateCalc >= debut && dateCalc <= fin) {
          baremeApplicable = bareme;
          break;
        }
      }
      if (!baremeApplicable) {
        setCalcData(prev => ({ ...prev, revenuRepute: null, contributionMax: "Barème non trouvé pour cette date" }));
        return;
      }

      let revC = baremeApplicable.Seuil;
      if (calcData.regionEloignee) revC = revC * valRegEloiMulti;
      let revE = calcData.revenu - revC;
      if (revE < 0) revE = 0;
      let actE = calcData.inclutResidence ? calcData.biens - valDeducMaison : calcData.biens - valDeducSansMaison;
      if (actE < 0) actE = 0;
      let liqE = calcData.categorieReq < 1 ? calcData.liquidite - valLiquideSeul : calcData.liquidite - valLiquideFamille;
      if (liqE < 0) liqE = 0;
      const revR = revC + revE + (actE * valActifMulti) + liqE;

      const volets = [
        { field: 'Volet0', value: 0 }, { field: 'Volet100', value: 100 }, { field: 'Volet200', value: 200 },
        { field: 'Volet300', value: 300 }, { field: 'Volet400', value: 400 }, { field: 'Volet500', value: 500 },
        { field: 'Volet600', value: 600 }, { field: 'Volet700', value: 700 }, { field: 'Volet800', value: 800 },
      ];
      let cMax;
      if (revR < baremeApplicable.Volet0) {
        cMax = 0;
      } else {
        cMax = 999;
        for (const volet of volets) {
          let seuil = baremeApplicable[volet.field];
          if (calcData.regionEloignee) seuil = seuil * valRegEloiMulti;
          if (revR < seuil) { cMax = volet.value; break; }
        }
      }

      let contributionText;
      if (cMax === 999) contributionText = "Non admissible";
      else if (cMax === 0) contributionText = "Admissibilité gratuite";
      else contributionText = `${(cMax + 50).toLocaleString('fr-CA')} $`;

      setCalcData(prev => ({ ...prev, revenuRepute: revR, contributionMax: contributionText }));
    } catch (error) {
      console.error('Erreur calcul:', error);
      setCalcData(prev => ({ ...prev, revenuRepute: null, contributionMax: "Erreur lors du calcul" }));
    }
  };

  const formatMontant = (value) => {
    return parseFloat(value || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' $';
  };

  return (
    <div className="win-modal-overlay" onClick={onClose}>
      <div className="calc-modal" onClick={e => e.stopPropagation()}>
        <div className="calc-header">
          <span>Calcul de l'admissibilité à l'Aide Juridique</span>
          <button className="win-modal-close" onClick={onClose}>{'×'}</button>
        </div>
        <div className="calc-content">
          <div className="calc-row">
            <label className="calc-label" style={{ minWidth: 380 }}>Date du calcul :</label>
            <DateInput value={calcData.dateCalcul} onChange={v => handleCalcChange('dateCalcul', v)} testId="calc-date-calcul" style={{ width: 220 }} />
          </div>
          <div className="calc-row">
            <label className="calc-label" style={{ minWidth: 380 }}>Catégorie de requérant :</label>
            <select className="calc-select" style={{ width: 300 }} value={calcData.categorieReq} onChange={e => handleCalcChange('categorieReq', parseInt(e.target.value))}>
              {categoriesCalc.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
            </select>
          </div>
          {/* Les 2 cases à cocher sur la même ligne, sous la catégorie */}
          <div className="calc-row">
            <label className="calc-label" style={{ minWidth: 380 }}></label>
            <label className="calc-checkbox" data-testid="calc-chk-region-eloignee"><input type="checkbox" checked={calcData.regionEloignee} onChange={e => handleCalcChange('regionEloignee', e.target.checked)} /> Région éloignée</label>
            <label className="calc-checkbox" data-testid="calc-chk-inclut-residence"><input type="checkbox" checked={calcData.inclutResidence} onChange={e => handleCalcChange('inclutResidence', e.target.checked)} /> Inclut la résidence</label>
          </div>
          {/* Section : montants à entrer (ligne bleue) */}
          <div className="calc-row result">
            <label className="calc-label" style={{ minWidth: 380 }}>Revenu annuel aux fins de l'admissibilité financière :</label>
            <MontantInput value={calcData.revenu} onChange={v => handleCalcChange('revenu', v)} testId="calc-revenu" />
          </div>
          <div className="calc-row">
            <label className="calc-label" style={{ minWidth: 380 }}>Total des biens :</label>
            <MontantInput value={calcData.biens} onChange={v => handleCalcChange('biens', v)} testId="calc-biens" />
          </div>
          <div className="calc-row">
            <label className="calc-label" style={{ minWidth: 380 }}>Total des liquidités :</label>
            <MontantInput value={calcData.liquidite} onChange={v => handleCalcChange('liquidite', v)} testId="calc-liquidite" />
          </div>
          {/* Section : résultats (ligne bleue) — Revenu réputé + Contribution max ensemble */}
          <div className="calc-row result">
            <label className="calc-label" style={{ minWidth: 380 }}>Revenu réputé pour le volet contributif :</label>
            <input type="text" className="calc-input-result" style={{ width: 220 }} value={calcData.revenuRepute !== null ? formatMontant(calcData.revenuRepute) : ''} readOnly />
          </div>
          <div className="calc-row">
            <label className="calc-label" style={{ minWidth: 380 }}>Contribution maximale incluant les frais d'administration de 50$ :</label>
            <input type="text" className={`calc-input-result-large ${calcData.contributionMax === 'Admissibilité gratuite' ? 'gratuit' : calcData.contributionMax === 'Non admissible' ? 'refuse' : ''}`} style={{ width: 220, minWidth: 220 }} value={calcData.contributionMax || ''} readOnly />
          </div>
          <div className="calc-buttons">
            <button className="calc-btn" onClick={handleCalculerAdmissibilite}>Calculer</button>
            <button className="calc-btn" onClick={handleInitialiser}>Initialiser</button>
            <button className="calc-btn" onClick={onClose}>Quitter</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculatriceModal;
